import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { BedDouble, Bath, Maximize, ArrowRight } from 'lucide-react';
import axios from 'axios';
import Footer from '../components/sections/Footer';
import SEO from '../components/SEO';
import { getOrganizationSchema, getBreadcrumbSchema } from '../seo/schemas';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FloorPlans = () => {
  const { t, i18n } = useTranslation();
  const isZh = i18n.language === 'zh';
  const navigate = useNavigate();
  const [filter, setFilter] = useState(null);
  const [sqftFilter, setSqftFilter] = useState('');
  const [products, setProducts] = useState([]);

  useEffect(() => {
    const load = async () => {
      try {
        const r = await axios.get(`${API}/admin/products`);
        setProducts((r.data.products || []).filter(p => p.is_online !== false));
      } catch {}
    };
    load();
  }, []);

  const filtered = products.filter(p => {
    if (filter && p.bedrooms !== filter) return false;
    if (sqftFilter && p.sqft > parseInt(sqftFilter)) return false;
    return true;
  });

  const bedOptions = [...new Set(products.map(p => p.bedrooms))].sort();
  const sqftOptions = [...new Set(products.map(p => p.sqft))].sort((a, b) => a - b);

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title={isZh ? 'ADU 户型方案 1-3 卧 500-1200 平尺 | 加州 ADU 户型图 VR 体验 | ALIGCUS 阿里建筑' : 'ADU Floor Plans 1-3 Bedroom 500-1200 sqft | California ADU Designs with VR Tour | ALIGCUS'}
        description={isZh ? '浏览 ALIGCUS 全部 ADU 户型方案：1-3 卧、500-1200 平尺，多款支持 VR 看房。每款户型可定制以贴合您的物业和生活。免费咨询 (626) 803-8000。' : 'Explore all ALIGCUS ADU floor plans: 1-3 bedrooms, 500-1200 sqft, with VR tours available. Customizable to fit your property. Free consultation (626) 803-8000.'}
        canonical={isZh ? 'https://www.aligcus.com/zh/floor-plans' : 'https://www.aligcus.com/floor-plans'}
        lang={isZh ? 'zh' : 'en'}
        alternates={[
          { lang: 'en', url: 'https://www.aligcus.com/floor-plans' },
          { lang: 'zh-CN', url: 'https://www.aligcus.com/zh/floor-plans' },
        ]}
        schemas={[
          getOrganizationSchema(isZh ? 'zh' : 'en'),
          getBreadcrumbSchema([
            { name: isZh ? '首页' : 'Home', url: isZh ? 'https://www.aligcus.com/zh/' : 'https://www.aligcus.com/' },
            { name: isZh ? 'ADU 户型' : 'ADU Floor Plans', url: isZh ? 'https://www.aligcus.com/zh/floor-plans' : 'https://www.aligcus.com/floor-plans' },
          ]),
        ]}
      />
      {/* Hero */}
      <section className="relative bg-gray-900 text-white py-20 pt-28">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <h1 className="text-4xl sm:text-5xl font-bold mb-4">{isZh ? '全部ADU产品' : 'All ADU Products'}</h1>
          <p className="text-lg text-gray-300 max-w-2xl mx-auto">
            {isZh ? '浏览我们所有的ADU户型，从500到1,200平方英尺。每个型号均可根据您的需求定制。' : 'Explore all our ADU models from 500 to 1,200 sq ft. Each model is customizable to fit your property and lifestyle.'}
          </p>
        </div>
      </section>

      {/* Filter Chips */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="flex flex-wrap gap-3 items-center justify-center mb-12">
          <button onClick={() => setFilter(null)} className={`px-5 py-2 rounded-full text-sm font-medium transition-all ${!filter ? 'bg-red-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>
            {isZh ? '全部' : 'All'}
          </button>
          {bedOptions.map(beds => (
            <button key={beds} onClick={() => setFilter(beds)} className={`px-5 py-2 rounded-full text-sm font-medium transition-all ${filter === beds ? 'bg-red-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>
              {beds} {isZh ? '卧' : 'Bedroom'}
            </button>
          ))}
          <span className="text-gray-400 mx-1">|</span>
          <span className="text-sm font-medium text-gray-600">Sq.ft: <span className="text-red-600 font-bold">{sqftFilter || (isZh ? '不限' : 'All')}</span></span>
          <input type="range" min={Math.min(...sqftOptions, 300)} max={Math.max(...sqftOptions, 1200)} step={50} value={sqftFilter || Math.max(...sqftOptions, 1200)} onChange={e => setSqftFilter(e.target.value)} className="w-40 accent-red-600" />
        </div>

        {/* Product Cards */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filtered.map(p => (
            <div key={p.id} className="group bg-white rounded-xl border border-gray-200 overflow-hidden shadow-md hover:shadow-2xl transition-all duration-500 hover:-translate-y-1 cursor-pointer" onClick={() => navigate(`/products/${p.id}`)}>
              <div className="relative h-56 overflow-hidden">
                <img src={p.images?.[0] || ''} alt={isZh ? p.title_zh : p.title_en} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                <div className="absolute top-4 left-4 bg-red-600 text-white px-3 py-1 rounded-full text-sm font-bold">{p.sqft} sq ft</div>
                {p.vr_link && (
                  <a href={p.vr_link} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()} className="absolute top-4 right-4 bg-red-600 text-white text-xs font-bold px-3 py-1.5 rounded shadow-lg hover:bg-red-700">VR</a>
                )}
              </div>
              <div className="p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-1">{isZh ? p.title_zh : p.title_en}</h3>
                <p className="text-sm text-gray-500 mb-4">{isZh ? p.desc_zh : p.desc_en}</p>
                <div className="flex items-center gap-4 text-sm text-gray-600 mb-4">
                  <span className="flex items-center gap-1"><BedDouble className="w-4 h-4 text-red-600" />{p.bedrooms} {isZh ? '卧' : 'Bed'}</span>
                  <span className="flex items-center gap-1"><Bath className="w-4 h-4 text-red-600" />{p.bathrooms} {isZh ? '卫' : 'Bath'}</span>
                  <span className="flex items-center gap-1"><Maximize className="w-4 h-4 text-red-600" />{p.sqft} sqft</span>
                </div>
                <Button className="w-full bg-red-600 hover:bg-red-700 text-white font-semibold" onClick={(e) => { e.stopPropagation(); navigate('/quote', { state: { bedrooms: p.bedrooms, bathrooms: p.bathrooms, square_footage: p.sqft } }); }}>
                  {isZh ? '获取报价' : 'Get Quote'} <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default FloorPlans;
